import type { MouseEvent, ReactNode } from 'react';
import {
  BarChart3,
  Building2,
  Code2,
  FileText,
  HelpCircle,
  ListTree,
  MessagesSquare,
  Palette,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type AdminTab =
  | 'property'
  | 'keytopic'
  | 'faq'
  | 'knowledge'
  | 'session_history'
  | 'analytics'
  | 'chattheme'
  | 'scriptshow';

interface NavItem {
  id: AdminTab;
  label: string;
  icon: ReactNode;
  group: 'Content' | 'Insights' | 'Setup';
}

export const ADMIN_NAV_ITEMS: readonly NavItem[] = [
  { id: 'property', label: 'Property Information', icon: <Building2 />, group: 'Content' },
  { id: 'keytopic', label: 'Key Topics', icon: <ListTree />, group: 'Content' },
  { id: 'faq', label: 'FAQs', icon: <HelpCircle />, group: 'Content' },
  { id: 'knowledge', label: 'API & Documents', icon: <FileText />, group: 'Content' },
  { id: 'session_history', label: 'Chat History', icon: <MessagesSquare />, group: 'Insights' },
  { id: 'analytics', label: 'Analytics', icon: <BarChart3 />, group: 'Insights' },
  { id: 'chattheme', label: 'Customisation', icon: <Palette />, group: 'Setup' },
  { id: 'scriptshow', label: 'Chatbot Script', icon: <Code2 />, group: 'Setup' },
];

const GROUPS = ['Content', 'Insights', 'Setup'] as const;

interface AdminNavProps {
  activeTab: string;
  onSelect: (tab: AdminTab, e: MouseEvent<HTMLButtonElement>) => void;
  layout: 'vertical' | 'horizontal';
  footer?: ReactNode;
}

function NavButton({
  item,
  active,
  compact,
  onSelect,
}: Readonly<{
  item: NavItem;
  active: boolean;
  compact: boolean;
  onSelect: AdminNavProps['onSelect'];
}>) {
  return (
    <button
      type="button"
      data-slot="admin-nav-item"
      aria-current={active ? 'page' : undefined}
      onClick={(e) => onSelect(item.id, e)}
      className={cn(
        'flex items-center gap-2.5 rounded-md text-sm font-medium outline-none transition-colors',
        'focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:size-4 [&_svg]:shrink-0',
        compact ? 'shrink-0 whitespace-nowrap px-3 py-2' : 'w-full px-3 py-2 text-left',
        active
          ? 'bg-secondary text-secondary-foreground [&_svg]:text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {item.icon}
      <span>{item.label}</span>
    </button>
  );
}

export function AdminNav({ activeTab, onSelect, layout, footer }: Readonly<AdminNavProps>) {
  if (layout === 'horizontal') {
    return (
      <nav
        data-slot="admin-nav"
        aria-label="Chatbot settings"
        className="flex items-center gap-2 rounded-xl border border-border bg-card p-2 shadow-xs"
      >
        <div data-slot="admin-nav-scroller" className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
          {ADMIN_NAV_ITEMS.map((item) => (
            <NavButton
              key={item.id}
              item={item}
              active={activeTab === item.id}
              compact
              onSelect={onSelect}
            />
          ))}
        </div>
        {footer && <div className="shrink-0">{footer}</div>}
      </nav>
    );
  }

  return (
    <nav
      data-slot="admin-nav"
      aria-label="Chatbot settings"
      className="sticky top-4 flex w-60 shrink-0 flex-col gap-4 self-start rounded-xl border border-border bg-card p-3 shadow-xs"
    >
      {GROUPS.map((group) => (
        <div key={group} data-slot="admin-nav-group" className="flex flex-col gap-0.5">
          <div data-slot="admin-nav-group-label" className="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {group}
          </div>
          {ADMIN_NAV_ITEMS.filter((item) => item.group === group).map((item) => (
            <NavButton
              key={item.id}
              item={item}
              active={activeTab === item.id}
              compact={false}
              onSelect={onSelect}
            />
          ))}
        </div>
      ))}
      {footer && <div data-slot="admin-nav-footer" className="border-t border-border pt-3">{footer}</div>}
    </nav>
  );
}
